export const useProductStore = defineStore('productStore', () => {
    const { public: publicConfig } = useRuntimeConfig();

    const initialized = ref(false);
    const products = ref([]);

    const unloading = ref(false);

    const unload = () => {
        unloading.value = true;

        products.value = [];
        currentProduct.value = null;
        initialized.value = false;

        unloading.value = false;
    };

    // Current product that is selected
    const currentProduct = ref();

    // Product Uuid of selected product
    const currentProductUuid = computed(() => {
        return currentProduct.value?.uuid ?? null;
    });

    // Product Code of selected product
    const currentProductCode = computed(() => {
        return currentProduct.value?.foreign_product_code ?? null;
    });

    // Product Type of selected product
    const currentProductType = computed(() => {
        return currentProduct.value?.type ?? null;
    });

    const productCodes = computed(() =>
        products.value.map((item) => item.foreign_product_code),
    );

    const findProduct = (uuidOrProductCode) => {
        if (!uuidOrProductCode) {
            return null;
        }

        return products.value.find(
            (item) =>
                item.uuid === uuidOrProductCode ||
                item.foreign_product_code === uuidOrProductCode,
        );
    };

    /**
     * Fetch the cached products from the internal nuxt API
     *
     * @returns {Promise<void>}
     */
    const fetchCachedProducts = async () => {
        const response = await $fetch('/api/platform/products');

        if (!response.data) {
            return;
        }

        const { data } = response;

        products.value = data;
        initialized.value = true;
    };

    const fetchProducts = async (withAuth) => {
        const Authorization = withAuth.startsWith('Bearer ')
            ? withAuth
            : `Bearer ${withAuth}`;

        const response = await $fetch(
            `${publicConfig.appHost}/api/v1/products`,
            {
                method: 'GET',
                headers: {
                    Authorization,
                    Accept: 'application/json',
                    'Content-Type': 'application/json',
                    'X-Context-Platform-Public-Auth-Token':
                        publicConfig.platform.authToken,
                },
            },
        );

        if (!response.data) {
            return;
        }

        const { data } = response;
        products.value = data;
        initialized.value = true;
    };

    /**
     * Load the products for the user
     *
     * @param {boolean|string} withAuth - If true, fetch the products with auth
     * @param {boolean} force - If true, force the reload of products
     * @returns {Promise<void>}
     */
    const loadProducts = async (withAuth = false, force = false) => {
        if (initialized.value && !withAuth) {
            return;
        }

        // Fetch the products for the user
        if (withAuth) {
            return await fetchProducts(withAuth);
        }

        await fetchCachedProducts();
    };

    const { execute: updateLastUsed } = useNewApiFetch(
        '/api/v1/update-last-used',
        'update-last-used-product',
        {
            body: {
                type: 'product',
                value: currentProductUuid,
            },
            method: 'PATCH',
            immediate: false,
            watch: false,
        },
    );

    watch(currentProductUuid, (uuid) => {
        if (!uuid) {
            return;
        }

        updateLastUsed();
    });

    const setActive = (uuid) => {
        if (currentProduct.value?.uuid === uuid) {
            // If the current product is already active, do nothing
            return;
        }

        if (currentProduct.value) {
            // Deactivate the current product
            currentProduct.value.isActive = false;
        }

        uuid = uuid ? uuid : products.value[0]?.uuid;

        // If no uuid is provided, use the first product's uuid
        if (!uuid) {
            console.warn('No product uuid provided to setActive.');
            return;
        }

        const product = findProduct(uuid);
        if (!product) {
            console.warn(`Product with uuid ${uuid} not found.`);
            return;
        }
        // Set the current product
        product.isActive = true;
        currentProduct.value = product;
    };

    return {
        // state
        products,
        productCodes,
        initialized,
        // computed
        currentProduct,
        currentProductUuid,
        currentProductCode,
        currentProductType,
        // methods
        setActive,
        loadProducts,
        unload,
    };
});
